import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api, mediaUrl, formatMoney } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Zap, Plus, Trash2, Clock, Crown, AlertTriangle, TrendingDown, RefreshCcw } from 'lucide-react';
import { SafeImage } from '@/components/marketplace/SafeImage';

const durationOptions = [
    { hours: 6, label: '6 horas' },
    { hours: 12, label: '12 horas' },
    { hours: 24, label: '24 horas' },
    { hours: 48, label: '48 horas' },
    { hours: 72, label: '3 días' },
    { hours: 168, label: '7 días' },
];

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

export default function DashboardFlashSales() {
    const [flashSales, setFlashSales] = useState([]);
    const [quota, setQuota] = useState(null);
    const [myAds, setMyAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ ad_id: '', discount_pct: 20, hours: 24 });
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data: fs }, { data: q }, { data: ads }] = await Promise.all([
                api.get('/me/flash-sales'),
                api.get('/me/plan/quota'),
                api.get('/me/ads'),
            ]);
            setFlashSales(fs || []);
            setQuota(q);
            setMyAds(ads || []);
        } catch (e) { toast.error('Error al cargar'); } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const create = async () => {
        if (!form.ad_id) { toast.error('Elige un anuncio'); return; }
        if (form.discount_pct <= 0 || form.discount_pct > 90) { toast.error('Descuento inválido (1-90%)'); return; }
        setCreating(true);
        try {
            const ends_at = new Date(Date.now() + form.hours * 60 * 60 * 1000).toISOString();
            await api.post('/me/flash-sales', { ad_id: form.ad_id, discount_pct: form.discount_pct, ends_at });
            toast.success('¡Oferta relámpago creada!');
            setOpen(false);
            setForm({ ad_id: '', discount_pct: 20, hours: 24 });
            load();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(detail || 'No se pudo crear');
            if (detail && (detail.includes('límite') || detail.includes('plan'))) {
                // stay open so user sees hint
            }
        } finally { setCreating(false); }
    };

    const remove = async (fs) => {
        if (!window.confirm('¿Cancelar esta oferta relámpago?')) return;
        try {
            await api.delete(`/me/flash-sales/${fs.id}`);
            toast.success('Oferta cancelada');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const usage = quota?.usage || {};
    const used = usage.flash_sales_used || 0;
    const limit = usage.flash_sales_limit || 0;
    const enabled = !!usage.flash_sales_enabled;
    const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;

    const now = Date.now();
    const isLive = (fs) => enabled && new Date(fs.starts_at).getTime() <= now && new Date(fs.ends_at).getTime() > now;

    return (
        <div className="space-y-4" data-testid="dashboard-flash-sales">
            <div className="flex items-center gap-3 flex-wrap">
                <Zap className="h-6 w-6 text-[hsl(var(--primary))]" />
                <h1 className="text-2xl font-semibold">Ofertas Relámpago</h1>
                {enabled && (
                    <Badge className="bg-emerald-100 text-emerald-800">Habilitadas</Badge>
                )}
                {!enabled && quota && (
                    <Badge variant="outline" className="border-amber-300 text-amber-700">Incluye en Starter o superior</Badge>
                )}
                <div className="ml-auto flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={load} data-testid="refresh-fs"><RefreshCcw className="h-3.5 w-3.5 mr-1" />Refrescar</Button>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button disabled={!enabled || (limit > 0 && used >= limit)} data-testid="new-flash-sale-btn">
                                <Plus className="h-4 w-4 mr-1" />Nueva oferta
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle>Crear oferta relámpago</DialogTitle></DialogHeader>
                            <div className="space-y-3">
                                <div>
                                    <Label>Anuncio</Label>
                                    <Select value={form.ad_id} onValueChange={(v) => setForm({ ...form, ad_id: v })}>
                                        <SelectTrigger data-testid="fs-ad-select"><SelectValue placeholder="Elige un anuncio publicado" /></SelectTrigger>
                                        <SelectContent>
                                            {myAds.filter(a => a.status === 'published').map(a => (
                                                <SelectItem key={a.id} value={a.id}>{a.title} — ${a.price}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <Label>Descuento (%)</Label>
                                        <Input type="number" min="1" max="90" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: parseInt(e.target.value) || 0 })} data-testid="fs-discount" />
                                    </div>
                                    <div>
                                        <Label>Duración</Label>
                                        <Select value={String(form.hours)} onValueChange={(v) => setForm({ ...form, hours: parseInt(v) })}>
                                            <SelectTrigger data-testid="fs-duration"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {durationOptions.map(o => <SelectItem key={o.hours} value={String(o.hours)}>{o.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                {form.ad_id && (() => {
                                    const ad = myAds.find(a => a.id === form.ad_id);
                                    if (!ad) return null;
                                    const finalPrice = ad.price * (1 - form.discount_pct / 100);
                                    return (
                                        <div className="p-3 bg-slate-50 rounded text-sm">
                                            <div>Precio actual: <span className="line-through">${ad.price}</span></div>
                                            <div>Precio de oferta: <strong className="text-emerald-700">${finalPrice.toFixed(2)}</strong> ({form.discount_pct}% off)</div>
                                            <div className="text-xs text-muted-foreground mt-1">Termina: {fmtDate(new Date(Date.now() + form.hours * 3600000).toISOString())}</div>
                                        </div>
                                    );
                                })()}
                            </div>
                            <DialogFooter>
                                <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
                                <Button onClick={create} disabled={creating || !form.ad_id} data-testid="fs-create-confirm">{creating ? 'Creando…' : 'Publicar oferta'}</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {!enabled && (
                <Alert className="border-amber-200 bg-amber-50">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                        Tu plan actual (<strong>{quota?.plan_name || quota?.plan_slug}</strong>) no incluye Ofertas Relámpago. <Link to="/planes" className="underline text-primary">Actualiza a Starter o superior</Link> para crear ofertas.
                    </AlertDescription>
                </Alert>
            )}

            {enabled && (
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-sm">
                                <span className="font-semibold">Uso del mes:</span> {used} de {limit === 0 ? '∞' : limit} ofertas
                            </div>
                            <Badge variant="outline"><Crown className="h-3 w-3 mr-1" />Plan {quota?.plan_slug}</Badge>
                        </div>
                        {limit > 0 && (
                            <>
                                <Progress value={pct} className="h-2" data-testid="fs-quota-bar" />
                                {pct >= 80 && <div className="text-xs text-amber-700 mt-1">Estás cerca del límite — considera <Link to="/planes" className="underline">subir de plan</Link>.</div>}
                            </>
                        )}
                    </CardContent>
                </Card>
            )}

            {loading ? (
                <div className="text-center text-sm text-muted-foreground py-10">Cargando…</div>
            ) : flashSales.length === 0 ? (
                <div className="text-center py-10 space-y-2">
                    <Zap className="h-8 w-8 mx-auto text-slate-400" />
                    <p className="text-sm text-muted-foreground">Aún no has creado ninguna oferta relámpago.</p>
                    {enabled && <Button size="sm" onClick={() => setOpen(true)} data-testid="empty-fs-cta"><Plus className="h-4 w-4 mr-1" />Crear la primera</Button>}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {flashSales.map(fs => {
                        const live = isLive(fs);
                        return (
                            <Card key={fs.id} className={live ? 'border-emerald-300 shadow' : ''} data-testid={`fs-item-${fs.id}`}>
                                <CardContent className="p-3">
                                    <div className="flex gap-3">
                                        <div className="w-16 h-16 rounded overflow-hidden bg-slate-100 shrink-0">
                                            <SafeImage src={mediaUrl(fs.ad?.media?.[0]?.url)} alt={fs.ad?.title} className="w-full h-full object-cover" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <Link to={`/ad/${fs.ad_id}`} className="text-sm font-medium hover:underline line-clamp-1">{fs.ad?.title || fs.ad_id}</Link>
                                            <div className="flex items-center gap-2 mt-1">
                                                <Badge className="bg-red-100 text-red-800 flex items-center gap-1"><TrendingDown className="h-3 w-3" />-{fs.discount_pct}%</Badge>
                                                {live ? <Badge className="bg-emerald-100 text-emerald-800">En vivo</Badge> : <Badge variant="outline">Programada</Badge>}
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                {fmtDate(fs.starts_at)} → {fmtDate(fs.ends_at)}
                                            </div>
                                            {fs.ad?.price && (
                                                <div className="text-xs mt-1">
                                                    <span className="line-through text-muted-foreground">{formatMoney(fs.ad.price)}</span>
                                                    <span className="ml-2 font-semibold text-emerald-700">{formatMoney(fs.ad.price * (1 - fs.discount_pct / 100))}</span>
                                                </div>
                                            )}
                                        </div>
                                        <Button size="sm" variant="ghost" onClick={() => remove(fs)} data-testid={`fs-delete-${fs.id}`}>
                                            <Trash2 className="h-4 w-4 text-destructive" />
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
